/*
 * A document page's commands in the one shortcut registry (DV S-13 §3.11, Appendix E; S-01 §3.17):
 * the scope `document`, which the page pushes while a document is open, and the scopes of the
 * document's own surfaces. The keys inside the text are not here: they are `DOC_CHORDS`
 * (`./keys.ts`), which the text's keymap answers before the registry hears the stroke.
 *
 * These bindings are core's — documents are a core area — so they are part of `coreShortcuts`
 * (`core/shell/shortcuts.ts`) and of the sheet from the first boot, and this file stays as small as
 * a list: it is in the shell's chunk, which is why it imports neither the editor nor the document's
 * copy. Their words are the document's (`doc.keys.*`), which the sheet's document section loads
 * with itself (`KeySheet.tsx`).
 *
 * **Scopes and stacks.** The page's stack is `global`, `document` and one of the page's surfaces:
 * the text (`docEditor`, `DOC_CHORDS` — named a scope only for the collision test, the way the
 * message box's chords are `composer`), an active comment card (`docComments`) or the version
 * history (`docHistory`). The three surfaces are never one stroke's at once — a card's letters fire
 * only on an active card, outside the text — so each is its own stack, and a card's `J` may mean
 * what the history's `J` means. The contents panel's tree keys are its own `keydown`
 * (`contents/contentsTree.ts`' `contentsKey`).
 *
 * **A definition is not a behaviour.** The registry is built once per boot; what "find" does
 * depends on the page on screen. Each binding runs the command the open page bound
 * (`bindDocumentCommands`); with no document open the scope is not on the stack, and a command
 * nobody bound does nothing.
 */

import type { ShortcutDef } from "../modules/registry.ts"

/** The scopes of a document page. */
export const DOC_SCOPES = {
	document: "document",
	editor: "docEditor",
	comments: "docComments",
	history: "docHistory",
} as const

/** Which of the page's scopes can be open together, innermost last (`CORE_SCOPE_STACKS`). */
export const DOCUMENT_SCOPE_STACKS: readonly (readonly string[])[] = [
	["global", DOC_SCOPES.document, DOC_SCOPES.editor],
	["global", DOC_SCOPES.document, DOC_SCOPES.comments],
	["global", DOC_SCOPES.document, DOC_SCOPES.history],
]

/** What the page's keys ask the open document to do. */
export type DocumentCommand =
	| "find"
	| "replace"
	| "suggest"
	| "edit"
	| "view"
	| "history"
	| "toggleComments"
	| "print"
	| "nextTab"
	| "previousTab"

const bound = new Map<DocumentCommand, () => void>()

/**
 * Lends the registry's keys the open page's commands, each by the package that builds it (the find
 * bar its two, the history its one); returns the call that takes them back, which a command's
 * owner makes when it unmounts. A later binding of one command replaces an earlier one, and taking
 * back leaves a command that another binding replaced alone.
 */
export function bindDocumentCommands(
	commands: Partial<Record<DocumentCommand, () => void>>,
): () => void {
	const mine = Object.entries(commands) as [DocumentCommand, () => void][]
	for (const [name, run] of mine) bound.set(name, run)
	return () => {
		for (const [name, run] of mine) if (bound.get(name) === run) bound.delete(name)
	}
}

/** Runs one of the page's commands, if the open page has it. */
export function runDocumentCommand(name: DocumentCommand): void {
	bound.get(name)?.()
}

/**
 * The `document` scope's bindings (S-13 Appendix E's registry table). Letters are the printed
 * letter (S-13-D9: Editing is Ctrl+Alt+Shift+Z on a German keyboard too), which falls back to the
 * key's position where the layout prints none — a Mac's Option with Shift, which turns every letter
 * into another character. Replace is `Ctrl+H` off the Mac and `Cmd+Shift+H` on it, where `Ctrl+H`
 * deletes a character. `Mod+P` opens the document's print view (`header/ExportMenu.tsx`), which prints
 * the document clean, in place of the browser's print of the whole page. `Alt+Shift+PageDown` and
 * `PageUp` go to the next and the previous tab of a document with two or more (W-2 §6,
 * `tabs/useDocTabs.ts`).
 */
export function documentShortcuts(mac: boolean): ShortcutDef[] {
	const def = (command: DocumentCommand, keys: string[], labelKey: string): ShortcutDef => ({
		id: `doc.${command}`,
		keys,
		scope: DOC_SCOPES.document,
		labelKey,
		run: () => runDocumentCommand(command),
	})
	return [
		def("find", ["Mod+F"], "doc.keys.find"),
		def("replace", [mac ? "Cmd+Shift+H" : "Ctrl+H"], "doc.keys.replace"),
		def("suggest", ["Mod+Alt+Shift+X"], "doc.keys.suggest"),
		def("edit", ["Mod+Alt+Shift+Z"], "doc.keys.edit"),
		def("view", ["Mod+Alt+Shift+C"], "doc.keys.view"),
		def("history", ["Mod+Alt+Shift+H"], "doc.keys.history"),
		def("toggleComments", ["Alt+Shift+C"], "doc.keys.toggleComments"),
		def("print", ["Mod+P"], "doc.keys.print"),
		def("nextTab", ["Alt+Shift+PageDown"], "doc.keys.nextTab"),
		def("previousTab", ["Alt+Shift+PageUp"], "doc.keys.previousTab"),
	]
}
